/**
 * Hook for managing messages in a conversation.
 * Fetches from Supabase messages table; realtime on `messages` + `contacts` for this thread; 60s poll fallback.
 */

import axios from "axios"
import { useState, useEffect, useCallback, useRef } from "react"
import { supabase } from "@/lib/data"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { getCurrentUser } from "@/lib/hooks/useUser"
import { agents } from "@/lib/agents"
import { integrations } from "@/lib/agents/integrations"
import { codec } from "@/lib/agents/integrations/encoder"
import { contacts } from "@/lib/agents/integrations/contacts"
import { ModerationMessage, ModerationContact, UseMessagesResult, DetectedItem } from "../types"

const CONTACT_SELECT_COLUMNS = "id, name, avatar, type, code, team, data, moderation_data, hitl, created_at, severity, summary, lasteval, evaluation, no_reply_needed, internal_comments"

type TelerivetDeliveryStatus =
  | "ignored"
  | "processing"
  | "received"
  | "sent"
  | "queued"
  | "failed"
  | "failed_queued"
  | "cancelled"
  | "delivered"
  | "not_delivered"
  | "read"

const TERMINAL_TELERIVET_STATUSES = new Set<TelerivetDeliveryStatus>([
  "failed",
  "failed_queued",
  "cancelled",
  "not_delivered",
  "read",
])

const DECORSIFY_PROXY = "https://signpost-ia-app-qa.azurewebsites.net/decorsify/"
const MAX_TELERIVET_TEXT_LENGTH = 4096

async function parseJsonSafe(response: Response): Promise<unknown> {
  const raw = await response.text()
  if (!raw || !raw.trim()) return null
  try {
    return JSON.parse(raw) as unknown
  } catch {
    throw new Error("Invalid JSON response from request")
  }
}

async function callTelerivetApi(
  url: string,
  method: "GET" | "POST",
  data?: Record<string, unknown>
): Promise<Record<string, unknown> | unknown[]> {
  const buildTelerivetError = (status: number, parsed: unknown, fallbackText?: string): Error => {
    let errorMessage = fallbackText || `Telerivet request failed (${status})`
    let errorCode: number | undefined

    if (parsed && typeof parsed === "object") {
      const asObj = parsed as Record<string, unknown>
      const directMessage = typeof asObj.message === "string" ? asObj.message : undefined
      const directCode = typeof asObj.code === "number" ? asObj.code : undefined
      const nestedError = (asObj.error && typeof asObj.error === "object")
        ? asObj.error as Record<string, unknown>
        : null
      const nestedMessage = nestedError && typeof nestedError.message === "string" ? nestedError.message : undefined
      const nestedCode = nestedError && typeof nestedError.code === "number" ? nestedError.code : undefined

      errorMessage = nestedMessage || directMessage || errorMessage
      errorCode = nestedCode ?? directCode
    }

    const err = new Error(errorMessage)
    ;(err as any).telerivetMeta = {
      status: "failed",
      error_message: errorMessage,
      error_code: errorCode,
      http_status: status,
    }
    return err
  }

  const requestInit: RequestInit = {
    method,
    headers: { "Content-Type": "application/json" },
    ...(data ? { body: JSON.stringify(data) } : {}),
  }

  // Always use Decorsify proxy in the browser — direct Telerivet calls fail with CORS.
  const proxiedUrl = `${DECORSIFY_PROXY}${url}`
  const proxiedResp = await axios.request({
    url: proxiedUrl,
    method,
    headers: { "Content-Type": "application/json" },
    ...(data ? { data } : {}),
    validateStatus: () => true,
  })

  if (proxiedResp.status < 200 || proxiedResp.status >= 300) {
    throw buildTelerivetError(proxiedResp.status, proxiedResp.data)
  }

  return (proxiedResp.data ?? {}) as Record<string, unknown> | unknown[]
}

function normalizeIntegrationPayload(payload: IntegrationPayload | null | undefined): IntegrationPayload {
  const raw = (payload || {}) as Record<string, unknown>
  const normalized: IntegrationPayload = { ...(payload || {}) }

  const pickString = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const value = raw[key]
      if (typeof value === "string" && value.trim()) return value
    }
    return undefined
  }

  normalized.type ||= pickString("type", "channel", "integrationChannel") as IntegrationsTypes | undefined
  normalized.apiKey ||= pickString("apiKey", "api_key", "telerivetApiKey", "telerivet_api_key")
  normalized.projectId ||= pickString("projectId", "project_id", "telerivetProjectId", "telerivet_project_id")
  normalized.phone ||= pickString("phone", "to_number", "phone_number", "toNumber")
  normalized.name ||= pickString("name", "contact_name")
  normalized.contact ||= pickString("contact", "contact_id", "uid")
  normalized.contact_id ||= pickString("contact_id", "contactId", "telerivet_contact_id")
  normalized.message_id ||= pickString("message_id", "messageId", "id")

  return normalized
}

function readProjectIdFromIntegration(input: unknown): string | undefined {
  if (!input || typeof input !== "object") return undefined
  const raw = input as Record<string, unknown>
  const candidates = [raw.projectId, raw.project_id, raw.telerivetProjectId, raw.telerivet_project_id]
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value
  }
  return undefined
}

function extractFirstStringByKeys(input: unknown, keys: string[]): string | undefined {
  const seen = new Set<unknown>()
  const queue: unknown[] = [input]

  while (queue.length > 0) {
    const node = queue.shift()
    if (!node || typeof node !== "object") continue
    if (seen.has(node)) continue
    seen.add(node)

    if (Array.isArray(node)) {
      for (const item of node) queue.push(item)
      continue
    }

    const record = node as Record<string, unknown>
    for (const key of keys) {
      const value = record[key]
      if (typeof value === "string" && value.trim()) return value
    }
    for (const value of Object.values(record)) queue.push(value)
  }
  return undefined
}

async function discoverTelerivetProjectId(apiKey: string): Promise<string | undefined> {
  try {
    const tryExtractProjectId = (value: unknown): string | undefined => {
      if (Array.isArray(value)) {
        const first = value.find(v => typeof (v as any)?.id === "string")
        return typeof (first as any)?.id === "string" ? (first as any).id : undefined
      }
      if (value && typeof value === "object") {
        const asObj = value as Record<string, unknown>
        const direct = asObj.id
        if (typeof direct === "string" && direct) return direct
        const nestedCandidates = [asObj.data, asObj.projects, asObj.results]
        for (const nested of nestedCandidates) {
          const nestedId = tryExtractProjectId(nested)
          if (nestedId) return nestedId
        }
      }
      return undefined
    }

    // Preferred: same auth style as send endpoint (api_key query param)
    const queryParamData = await callTelerivetApi(
      `https://api.telerivet.com/v1/projects?api_key=${encodeURIComponent(apiKey)}`,
      "GET"
    )
    const projectIdViaQuery = tryExtractProjectId(queryParamData)
    if (projectIdViaQuery) return projectIdViaQuery

    // Compatibility fallback: basic auth.
    const basicData = await callTelerivetApi(
      "https://api.telerivet.com/v1/projects",
      "GET"
    )
    const projectIdViaBasic = tryExtractProjectId(basicData)
    if (projectIdViaBasic) return projectIdViaBasic
    return undefined
  } catch {
    return undefined
  }
}

async function getHistoricalThreadProjectId(
  contactId: string,
  teamId: string
): Promise<string | undefined> {
  try {
    const { data } = await supabase
      .from("messages")
      .select("integration, channel")
      .eq("contact", contactId)
      .eq("team", teamId)
      .eq("channel", "telerivet")
      .order("created_at", { ascending: false })
      .limit(50)

    for (const row of data || []) {
      const projectId = readProjectIdFromIntegration((row as any).integration)
      if (projectId) return projectId
    }
    return undefined
  } catch {
    return undefined
  }
}

async function getHistoricalThreadRouteId(
  contactId: string,
  teamId: string
): Promise<string | undefined> {
  try {
    const { data } = await supabase
      .from("messages")
      .select("integration, channel")
      .eq("contact", contactId)
      .eq("team", teamId)
      .eq("channel", "telerivet")
      .order("created_at", { ascending: false })
      .limit(50)

    for (const row of data || []) {
      const integration = (row as any).integration
      const routeId =
        typeof integration?.route_id === "string" ? integration.route_id : undefined
      if (routeId && /^PN[a-zA-Z0-9]+$/.test(routeId)) return routeId
    }
    return undefined
  } catch {
    return undefined
  }
}

const telerivetContactFbmCache = new Map<string, string | null>()

async function getTelerivetFbmRecipientId(
  projectId: string,
  contactId: string,
  apiKey: string
): Promise<string | undefined> {
  const cacheKey = `${projectId}:${contactId}`
  if (telerivetContactFbmCache.has(cacheKey)) {
    const cached = telerivetContactFbmCache.get(cacheKey)
    return cached || undefined
  }

  try {
    const result = await callTelerivetApi(
      `https://api.telerivet.com/v1/projects/${projectId}/contacts/${contactId}?api_key=${encodeURIComponent(apiKey)}`,
      "GET"
    )
    const fbmId = extractFirstStringByKeys(result, ["fbm"])
    telerivetContactFbmCache.set(cacheKey, fbmId || null)
    return fbmId || undefined
  } catch {
    telerivetContactFbmCache.set(cacheKey, null)
    return undefined
  }
}

async function sendTelerivetMessage(
  content: string,
  payload: IntegrationPayload
): Promise<{
  id?: string
  status?: string
  time_updated?: number
  contact_id?: string
  error_message?: string
  error_code?: number
}> {
  const apiKey = payload.apiKey
  const projectId = payload.projectId
  const derivedContactId = (
    payload.contact_id ||
    (
      typeof payload.contact === "string" &&
      /^CT[a-zA-Z0-9]+$/.test(payload.contact)
        ? payload.contact
        : undefined
    )
  )
  if (!apiKey || !projectId) {
    throw new Error("Missing Telerivet integration data (apiKey, projectId).")
  }
  if (!payload.phone && !derivedContactId) {
    throw new Error("Missing Telerivet destination (phone or contact_id).")
  }

  const normalizedPhone = typeof payload.phone === "string" ? payload.phone.trim() : payload.phone
  // For chat-app contacts, values like PSID/fbm IDs can be present in `phone`.
  // When contact_id is available, avoid sending clearly non-phone `to_number`.
  const looksPhoneLike =
    typeof normalizedPhone === "string" && /^\+?[1-9]\d{6,14}$/.test(normalizedPhone)
  let effectivePhone = derivedContactId && !looksPhoneLike ? undefined : normalizedPhone

  // Facebook Messenger contacts require to_number to be prefixed as "fbm:<id>".
  // If we can resolve an fbm id for this contact, force that recipient format.
  if (derivedContactId && projectId && apiKey) {
    const fbmId = await getTelerivetFbmRecipientId(projectId, derivedContactId, apiKey)
    if (fbmId) effectivePhone = `fbm:${fbmId}`
  }

  const isFbmRecipient = typeof effectivePhone === "string" && effectivePhone.startsWith("fbm:")

  const result = await integrations.telerivet.sendMessage(content, {
    ...payload,
    phone: effectivePhone,
    // For Facebook Messenger sends via API, including contact_id together with
    // to_number=fbm:<id> can trigger "Recipient number empty".
    // Prefer explicit fbm recipient and omit contact_id in that case.
    contact_id: isFbmRecipient ? undefined : derivedContactId,
  })
  const data = (result && !Array.isArray(result) ? result : {}) as Record<string, unknown>
  return {
    id: typeof data.id === "string" ? data.id : undefined,
    status: typeof data.status === "string" ? data.status : undefined,
    time_updated: typeof data.time_updated === "number" ? data.time_updated : undefined,
    contact_id: typeof data.contact_id === "string" ? data.contact_id : derivedContactId,
    error_message: typeof data.error_message === "string" ? data.error_message : undefined,
    error_code: typeof data.error_code === "number" ? data.error_code : undefined,
  }
}

function splitTelerivetContent(content: string, maxLen = MAX_TELERIVET_TEXT_LENGTH): string[] {
  const text = content.trim()
  if (!text) return []
  if (text.length <= maxLen) return [text]

  const chunks: string[] = []
  let remaining = text

  while (remaining.length > maxLen) {
    // Prefer splitting on paragraph/newline boundaries, then whitespace, then hard cut.
    let splitAt = remaining.lastIndexOf("\n", maxLen)
    if (splitAt < Math.floor(maxLen * 0.5)) splitAt = remaining.lastIndexOf(" ", maxLen)
    if (splitAt < Math.floor(maxLen * 0.5)) splitAt = maxLen

    const part = remaining.slice(0, splitAt).trim()
    if (part) chunks.push(part)
    remaining = remaining.slice(splitAt).trim()
  }

  if (remaining) chunks.push(remaining)
  return chunks
}

async function getTelerivetMessageStatus(
  projectId: string,
  messageId: string,
  apiKey: string
): Promise<{ status?: string; time_updated?: number; error_message?: string; error_code?: number }> {
  const result = await callTelerivetApi(
    `https://api.telerivet.com/v1/projects/${projectId}/messages/${messageId}?api_key=${encodeURIComponent(apiKey)}`,
    "GET"
  )
  const data = (result && !Array.isArray(result) ? result : {}) as Record<string, unknown>
  return {
    status: typeof data.status === "string" ? data.status : undefined,
    time_updated: typeof data.time_updated === "number" ? data.time_updated : undefined,
    error_message: typeof data.error_message === "string" ? data.error_message : undefined,
    error_code: typeof data.error_code === "number" ? data.error_code : undefined,
  }
}

export function useMessages(contactId: string | null): UseMessagesResult & {
  contact: ModerationContact | null
} {
  const [messages, setMessages] = useState<ModerationMessage[]>([])
  const [contact, setContact] = useState<ModerationContact | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const statusSyncingRef = useRef(false)

  const lastFingerprintRef = useRef<string>("")
  const isPollingRef = useRef(false)
  const { selectedTeam } = useTeamStore()

  useEffect(() => {
    getCurrentUser().then(({ data }) => { if (data) setCurrentUser(data) })
  }, [])

  const resolveTelerivetPayload = useCallback(async (
    basePayload: IntegrationPayload,
    agentId: number | null | undefined,
    fallbackChannel: string | null | undefined
  ): Promise<IntegrationPayload> => {
    const next: IntegrationPayload = normalizeIntegrationPayload(basePayload)
    const inferredChannel = next.type || (fallbackChannel as IntegrationsTypes | null)
    if (inferredChannel) next.type = inferredChannel
    if (next.type !== "telerivet" || !selectedTeam?.id || !contactId) return next

    // 1) Prefer integration config from the agent's message worker.
    if ((!next.apiKey || !next.projectId) && agentId) {
      try {
        const agent = await agents.loadAgent(agentId as any, selectedTeam.id)
        if (agent) {
          const workers = Object.values(agent.workers || {}) as any[]
          const telerivetWorker = workers.find((w: any) =>
            w?.parameters?.integrationChannel === "telerivet" ||
            !!w?.parameters?.telerivetProjectId ||
            !!w?.parameters?.telerivetApiKey
          )
          if (telerivetWorker?.parameters) {
            next.apiKey ||= telerivetWorker.parameters.telerivetApiKey
            next.projectId ||= telerivetWorker.parameters.telerivetProjectId
          }
        }

        // Fallback: inspect raw agent row JSON in case runtime worker params
        // do not expose deployment settings directly.
        if (!next.projectId || !next.apiKey) {
          const { data: agentRows } = await supabase
            .from("agents")
            .select("workers, config")
            .eq("id", agentId)
            .limit(1)
          const rawAgent = agentRows?.[0]
          if (rawAgent) {
            next.projectId ||= extractFirstStringByKeys(rawAgent, ["telerivetProjectId", "projectId", "project_id", "telerivet_project_id"])
            next.apiKey ||= extractFirstStringByKeys(rawAgent, ["telerivetApiKey", "apiKey", "api_key", "telerivet_api_key"])
          }
        }
      } catch {
        // best-effort
      }
    }

    // 2) Team-level API keys fallback (telerivet + codec).
    let codecKey: string | undefined
    try {
      const { data: keyRows } = await supabase
        .from("api_keys")
        .select("type, key")
        .eq("team_id", selectedTeam.id)
      const keyMap = new Map<string, string>()
      for (const row of keyRows || []) {
        if (row.type && row.key) keyMap.set(row.type, row.key)
      }
      next.apiKey ||= keyMap.get("telerivet")
      codecKey = keyMap.get("codec")
    } catch {
      // best-effort
    }

    // 2.5) If we have API key but no project, try discover from Telerivet account.
    if (!next.projectId && next.apiKey) {
      const discoveredProjectId = await discoverTelerivetProjectId(next.apiKey)
      if (discoveredProjectId) next.projectId = discoveredProjectId
    }

    // 3) Resolve destination phone if missing.
    if (!next.phone) {
      if (codecKey) {
        try {
          // For telerivet contacts, id is usually codec-encrypted phone digits.
          const decoded = await codec.decrypt(contactId, codecKey)
          if (decoded) next.phone = decoded
        } catch {
          // not codec-encrypted contact id
        }
      }

      if (!next.phone) {
        try {
          const { data: contactRows } = await supabase
            .from("contacts")
            .select("data")
            .eq("id", contactId)
            .limit(1)
          const parsed = await contacts.decrypt(contactRows?.[0], codecKey)
          if (parsed?.phone && typeof parsed.phone === "string") next.phone = parsed.phone
          if (parsed?.name && typeof parsed.name === "string") next.name ||= parsed.name
          if (parsed?.contact_id && typeof parsed.contact_id === "string") next.contact_id ||= parsed.contact_id
        } catch {
          // best-effort
        }
      }
    }

    if (!next.contact_id && codecKey) {
      try {
        const { data: contactRows } = await supabase
          .from("contacts")
          .select("data")
          .eq("id", contactId)
          .limit(1)
        const parsed = await contacts.decrypt(contactRows?.[0], codecKey)
        if (parsed?.contact_id && typeof parsed.contact_id === "string") next.contact_id = parsed.contact_id
      } catch {
        // best-effort
      }
    }

    next.contact ||= contactId
    return next
  }, [selectedTeam?.id, contactId])

  const refreshTelerivetStatuses = useCallback(async (msgs: ModerationMessage[]) => {
    if (!selectedTeam?.id || statusSyncingRef.current) return
    // Only check the single most recent non-terminal outbound Telerivet message.
    // Earlier messages never change status once superseded, and we only need to
    // know whether the latest send got through.
    const allNonTerminal = msgs.filter((m) => {
      if (m.channel !== "telerivet") return false
      if (m.role === "assistant" && !m.sender) {
        const mirroredStatus = typeof (m.integration as any)?.status === "string"
          ? String((m.integration as any).status).toLowerCase()
          : ""
        if (mirroredStatus === "received") return false
      }
      const integration = (m.integration || {}) as Record<string, unknown>
      const messageId = typeof integration.message_id === "string" ? integration.message_id : ""
      if (!messageId) return false
      const status = typeof integration.status === "string" ? integration.status as TelerivetDeliveryStatus : undefined
      return !status || !TERMINAL_TELERIVET_STATUSES.has(status)
    })
    const candidates = allNonTerminal.slice(-1) // most recent only
    if (candidates.length === 0) return

    statusSyncingRef.current = true
    try {
      const { data: keyRows } = await supabase
        .from("api_keys")
        .select("type, key")
        .eq("team_id", selectedTeam.id)
      const telerivetApiKey = (keyRows || []).find(r => r.type === "telerivet")?.key || ""
      if (!telerivetApiKey) return

      for (const msg of candidates) {
        try {
          const integration = (msg.integration || {}) as Record<string, unknown>
          const messageId = typeof integration.message_id === "string" ? integration.message_id : ""
          const projectId = (
            (typeof integration.projectId === "string" && integration.projectId) ||
            (typeof integration.project_id === "string" && integration.project_id) ||
            ""
          )
          if (!messageId || !projectId) continue

          const statusData = await getTelerivetMessageStatus(projectId, messageId, telerivetApiKey)
          if (!statusData.status && !statusData.time_updated) continue

          const currentStatus = typeof integration.status === "string" ? integration.status : undefined
          const currentTimeUpdated = typeof integration.time_updated === "number" ? integration.time_updated : undefined
          if (statusData.status === currentStatus && statusData.time_updated === currentTimeUpdated) continue

          const nextIntegration = {
            ...integration,
            status: statusData.status ?? currentStatus,
            time_updated: statusData.time_updated ?? currentTimeUpdated,
            error_message: statusData.error_message ?? (typeof integration.error_message === "string" ? integration.error_message : undefined),
            error_code: statusData.error_code ?? (typeof integration.error_code === "number" ? integration.error_code : undefined),
          }

          await supabase
            .from("messages")
            .update({ integration: nextIntegration as any })
            .eq("id", msg.id)

          setMessages(prev => prev.map(m =>
            m.id === msg.id ? { ...m, integration: nextIntegration as any } : m
          ))
        } catch {
          // best-effort
        }
      }
    } finally {
      statusSyncingRef.current = false
    }
  }, [selectedTeam?.id])

  const fetchMessages = useCallback(async (opts?: { silent?: boolean }) => {
    if (!contactId || !selectedTeam?.id) {
      setMessages([])
      return
    }

    const silent = opts?.silent === true
    const isPolling = isPollingRef.current
    if (!isPolling && !silent) setLoading(true)
    setError(null)

    try {
      const [messagesResult, contactResult] = await Promise.all([
        supabase
          .from("messages")
          .select("id, contact, role, message, channel, created_at, sender, agent, integration, user_detected_items, agent_detected_items, escalation_detected, escalation_from_level, escalation_to_level, escalation_reasoning, agent_appropriate, agent_concern_level, agent_reasoning, narrative_update, rating, rating_example, custom_message_flags, highrisk, lowconf, askhuman, sender_contact:contacts!messages_sender_fkey(id, name)")
          .eq("contact", contactId)
          .eq("team", selectedTeam.id)
          // Pull newest-first to ensure long threads include recent operator/bot replies.
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("contacts")
          .select(CONTACT_SELECT_COLUMNS)
          .eq("id", contactId)
          .single(),
      ])

      if (contactResult.data) {
        const contactData = contactResult.data as any
        setContact({
          ...contactData,
          evaluation: contactData.evaluation as any,
          internal_comments: contactData.internal_comments as any,
        })
      }

      const fetchedMessages: ModerationMessage[] = (messagesResult.data || []).map(r => ({
        ...r,
        user_detected_items: r.user_detected_items as unknown as DetectedItem[] | null,
        agent_detected_items: r.agent_detected_items as unknown as DetectedItem[] | null,
        custom_message_flags: (r as any).custom_message_flags as FlagDetectedItem[] | null,
        rating: (r.rating as "0" | "1" | null) ?? null,
        integration: (r as any).integration as any,
        senderContact: (() => {
          const senderContact = (r as any).sender_contact
          if (!senderContact) return null
          if (Array.isArray(senderContact)) return senderContact[0] ?? null
          return senderContact
        })(),
      }))
      const msgs = fetchedMessages.reverse()

      // Fingerprint to avoid unnecessary re-renders — include all eval fields
      const fingerprint = msgs.map(m =>
        `${m.id}:${m.rating || ""}:${m.rating_example || ""}:${JSON.stringify(m.user_detected_items || [])}:${JSON.stringify(m.agent_detected_items || [])}:${JSON.stringify(m.custom_message_flags || [])}:${m.highrisk ?? ""}:${m.lowconf ?? ""}:${m.askhuman ?? ""}:${m.agent_reasoning || ""}:${m.narrative_update || ""}:${m.agent_appropriate ?? ""}:${m.agent_concern_level ?? ""}:${JSON.stringify(m.integration || {})}`
      ).join(",")

      if (fingerprint !== lastFingerprintRef.current) {
        lastFingerprintRef.current = fingerprint
        setMessages(msgs)
      }

      void refreshTelerivetStatuses(msgs)
    } catch (err) {
      console.error("[useMessages] fetch failed:", err)
      if (!isPolling && !silent) {
        setError(err instanceof Error ? err.message : "Failed to fetch messages")
        setMessages([])
      }
    } finally {
      if (!isPolling && !silent) setLoading(false)
    }
  }, [contactId, selectedTeam?.id, refreshTelerivetStatuses])

  // Clear stale thread state the instant the contact changes, before the async
  // fetch resolves. Otherwise `messages` keeps the previous conversation's rows
  // during the fetch window, and anything derived from them (e.g. the handling
  // agent that scopes the Forms panel) is briefly reported for the wrong thread.
  useEffect(() => {
    setMessages([])
    setContact(null)
    lastFingerprintRef.current = ""
  }, [contactId])

  useEffect(() => {
    fetchMessages()
  }, [fetchMessages])

  // Realtime: thread updates when new/edited messages land (moderation + simulator)
  useEffect(() => {
    if (!contactId || !selectedTeam?.id) return

    let debounceTimer: ReturnType<typeof setTimeout> | undefined
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        fetchMessages({ silent: true })
      }, 200)
    }

    const channel = supabase
      .channel(`messages-thread:${contactId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `contact=eq.${contactId}`,
        },
        scheduleRefetch
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "contacts",
          filter: `id=eq.${contactId}`,
        },
        scheduleRefetch
      )
      .subscribe()

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      void supabase.removeChannel(channel)
    }
  }, [contactId, selectedTeam?.id, fetchMessages])

  // Fallback polling (realtime is primary)
  useEffect(() => {
    if (!contactId) return
    isPollingRef.current = true
    const interval = setInterval(() => fetchMessages({ silent: true }), 60000)
    return () => {
      clearInterval(interval)
      isPollingRef.current = false
    }
  }, [contactId, fetchMessages])

  const sendMessage = useCallback(async (
    content: string,
    senderContactId?: string
  ) => {
    if (!contactId || !selectedTeam?.id) throw new Error("No contact or team")

    // 1. Find the agent linked to this conversation (from the most recent bot message)
    const agentId = [...messages].reverse().find(m => m.role === "assistant" && m.agent)?.agent
    const lastChannel = [...messages].reverse().find(m => m.channel)?.channel
    const lastInboundChannel = [...messages].reverse().find(m => (m.role === "user" || m.role === "synthetic") && m.channel)?.channel
    const preferredChannel = lastInboundChannel || lastChannel

    // 2. Load integration payload for this active conversation.
    // Moderation reads persisted agent state as the canonical source.
    let integrationPayload: IntegrationPayload = {}
    try {
      const { data: stateRows } = await supabase
        .from("states")
        .select("state")
        .eq("id", contactId)
        .limit(1)
      const stateRow = stateRows?.[0]
      if (stateRow?.state) {
        const state = stateRow.state as any
        integrationPayload = normalizeIntegrationPayload(
          state?.agent?.integration ||
          state?.integration ||
          integrationPayload
        )
      }
    } catch {
      // rows may not exist for all contacts — that's fine
    }
    integrationPayload = await resolveTelerivetPayload(integrationPayload, agentId, preferredChannel)
    if (!integrationPayload.projectId) {
      // Moderation-only fallback: recover project id from existing thread messages.
      // This helps when state/agent payloads are partial but prior telerivet messages
      // in this conversation already include integration metadata.
      const historicalProjectId = [...messages]
        .reverse()
        .filter(m => m.channel === "telerivet")
        .map(m => readProjectIdFromIntegration(m.integration))
        .find((value): value is string => !!value)
      if (historicalProjectId) {
        integrationPayload.projectId = historicalProjectId
      }
    }
    if (!integrationPayload.projectId) {
      const dbHistoricalProjectId = await getHistoricalThreadProjectId(contactId, selectedTeam.id)
      if (dbHistoricalProjectId) {
        integrationPayload.projectId = dbHistoricalProjectId
      }
    }

    if (!integrationPayload.route_id) {
      const historicalRouteId = [...messages]
        .reverse()
        .filter(m => m.channel === "telerivet")
        .map(m => {
          const ri = (m.integration as any)?.route_id
          return typeof ri === "string" && /^PN[a-zA-Z0-9]+$/.test(ri) ? ri : undefined
        })
        .find((v): v is string => !!v)
      if (historicalRouteId) {
        integrationPayload.route_id = historicalRouteId
      }
    }
    if (!integrationPayload.route_id) {
      const dbHistoricalRouteId = await getHistoricalThreadRouteId(contactId, selectedTeam.id)
      if (dbHistoricalRouteId) {
        integrationPayload.route_id = dbHistoricalRouteId
      }
    }

    // 3. Write to messages table (our moderation/eval record)
    const outChannel = integrationPayload?.type || preferredChannel || "app"
    const persistedIntegration = {
      type: integrationPayload?.type,
      projectId: integrationPayload?.projectId,
      route_id: integrationPayload?.route_id,
      contact_id: integrationPayload?.contact_id,
      contact: integrationPayload?.contact,
      status: integrationPayload?.type === "telerivet" ? "queued" : undefined,
    }
    const { data, error } = await supabase
      .from("messages")
      .insert({
        contact: contactId,
        team: selectedTeam.id,
        message: content,
        role: "assistant",
        channel: outChannel,
        integration: persistedIntegration as any,
        sender: senderContactId || null,
        agent: agentId ?? null,
      })
      .select("id, contact, role, message, channel, created_at, sender, agent, integration, user_detected_items, agent_detected_items, escalation_detected, escalation_from_level, escalation_to_level, escalation_reasoning, agent_appropriate, agent_concern_level, agent_reasoning, narrative_update, rating, rating_example, custom_message_flags, highrisk, lowconf, askhuman, sender_contact:contacts!messages_sender_fkey(id, name)")
      .single()

    if (error) throw error

    if (data) {
      const newMsg: ModerationMessage = {
        ...data,
        user_detected_items: null,
        agent_detected_items: null,
        // Supabase row types use Json; ModerationMessage expects FlagDetectedItem[]
        custom_message_flags: null,
        highrisk: (data as { highrisk?: number | null }).highrisk ?? null,
        lowconf: (data as { lowconf?: number | null }).lowconf ?? null,
        askhuman: (data as { askhuman?: number | null }).askhuman ?? null,
        rating: (data.rating as "0" | "1" | null) ?? null,
        integration: (data as any).integration as any,
        senderContact: (() => {
          const senderContact = (data as any).sender_contact
          if (!senderContact) return null
          if (Array.isArray(senderContact)) return senderContact[0] ?? null
          return senderContact
        })(),
      }
      setMessages(prev => [...prev, newMsg])
    }

    // 4. Write to history table + deliver via integration channel (telerivet, app, etc.)
    //    This is what keeps the bot's conversation context in sync and delivers the message.
    let deliveryError: Error | null = null
    if (integrationPayload?.type === "telerivet") {
      try {
        const missingFields: string[] = []
        if (!integrationPayload.apiKey) missingFields.push("apiKey")
        if (!integrationPayload.projectId) missingFields.push("projectId")
        if (!integrationPayload.phone && !integrationPayload.contact_id) missingFields.push("phone/contact_id")
        if (missingFields.length > 0) {
          throw new Error(`Missing Telerivet integration data (${missingFields.join(", ")}).`)
        }
        const chunks = splitTelerivetContent(content)
        if (chunks.length === 0) {
          throw new Error("Cannot send an empty message.")
        }

        let firstDelivery: Awaited<ReturnType<typeof sendTelerivetMessage>> | null = null
        let lastDelivery: Awaited<ReturnType<typeof sendTelerivetMessage>> | null = null

        for (const chunk of chunks) {
          const delivery = await sendTelerivetMessage(chunk, integrationPayload)
          if (!firstDelivery) firstDelivery = delivery
          lastDelivery = delivery
        }

        const delivery = lastDelivery || firstDelivery
        if (!delivery) throw new Error("Failed to deliver message.")
        const statusPatch: Record<string, unknown> = {
          ...persistedIntegration,
          message_id: firstDelivery?.id || delivery.id,
          message_ids: chunks.length > 1
            ? [firstDelivery?.id, lastDelivery?.id].filter((id): id is string => typeof id === "string")
            : undefined,
          status: delivery.status || "sent",
          time_updated: delivery.time_updated,
          contact_id: delivery.contact_id || integrationPayload.contact_id,
          error_message: delivery.error_message,
          error_code: delivery.error_code,
        }

        await supabase
          .from("messages")
          .update({ integration: statusPatch as any })
          .eq("id", data.id)

        setMessages(prev => prev.map(m =>
          m.id === data.id ? { ...m, integration: statusPatch as any } : m
        ))
      } catch (err) {
        console.error("[useMessages] direct Telerivet send failed:", err)
        const errMeta = (err as any)?.telerivetMeta as {
          status?: string
          error_message?: string
          error_code?: number
        } | undefined
        const failedStatusPatch: Record<string, unknown> = {
          ...persistedIntegration,
          status: "failed",
          error_message: errMeta?.error_message || (err instanceof Error ? err.message : "Failed to send"),
          ...(typeof errMeta?.error_code === "number" ? { error_code: errMeta.error_code } : {}),
        }
        await supabase
          .from("messages")
          .update({ integration: failedStatusPatch as any })
          .eq("id", data.id)

        setMessages(prev => prev.map(m =>
          m.id === data.id ? { ...m, integration: failedStatusPatch as any } : m
        ))
        deliveryError = err instanceof Error
          ? err
          : new Error("Human reply was saved, but external delivery failed.")
      }
    }

    if (agentId) {
      try {
        const agent = await agents.loadAgent(agentId as any, selectedTeam.id)
        if (agent) {
          const historyOnlyPayload = integrationPayload?.type === "telerivet"
            ? { ...integrationPayload, type: "app" as IntegrationsTypes }
            : integrationPayload
          await agent.sendHumanHITLMessage(content, contactId, selectedTeam.id, false, historyOnlyPayload)
        }
      } catch (err) {
        console.error("[useMessages] sendHumanHITLMessage failed:", err)
        if (!deliveryError) {
          deliveryError = err instanceof Error
            ? err
            : new Error("Message delivered, but failed to sync agent history.")
        }
      }
    }

    if (deliveryError) throw deliveryError
  }, [contactId, selectedTeam?.id, messages, resolveTelerivetPayload])

  const optimisticUpdateMessage = useCallback((messageId: string, updates: Partial<ModerationMessage>) => {
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, ...updates } : m))
  }, [])

  return { messages, loading, error, sendMessage, refresh: fetchMessages, contact, optimisticUpdateMessage }
}
